import { BaseComponent } from 'src/scripts/components/base-component'
import { hexToColorName } from 'src/scripts/utils/color'
import { $el } from 'src/scripts/utils/utils'

const SWATCH_SELECTOR = '.color-swatch'

export class ShapeSwatch extends BaseComponent {
  element: HTMLElement

  constructor() {
    super()
    this.element = $el(SWATCH_SELECTOR, this) || this
    this.setValue()
  }

  setValue(): void {
    if (this.hasAttribute('is-image')) return

    const selectedValue = this.dataset.color?.toLowerCase().trim() || ''
    const patterns = (this.dataset.colorsPatterns || '').split('\n')

    if (!this.isColor(selectedValue)) {
      this.element.setAttribute('not-valid', '')
    }

    this.setPropertyValue(selectedValue)

    patterns.forEach(pattern => {
      const [patternLabel = '', patternValue = ''] = pattern.split('::')

      if (patternLabel.toLowerCase().trim() === selectedValue) {
        this.setPropertyValue(patternValue)
      }
    })
  }

  getGradientValue(colors: string[]): string | null {
    let notValidColor = 0
    const patterns = (this.dataset.colorsPatterns || '').split('\n')
    const preparedPatterns = patterns.reduce<Record<string, string>>(
      (result, pattern) => {
        const [patternLabel = '', patternValue = ''] = pattern.split('::')
        const key = patternLabel.toLowerCase().trim()
        if (key) result[key] = patternValue
        return result
      },
      {},
    )
    const preparedColors = colors.map(gradientColor => {
      const color = gradientColor.toLowerCase().trim()

      if (preparedPatterns[color]) return preparedPatterns[color]
      if (this.isColor(color)) return color

      notValidColor += 1
      return 'white'
    })

    if (notValidColor === colors.length) return null

    // 保持线上主题对 2、3、5 色产品色板的视觉停靠点。
    if (preparedColors.length === 2) {
      return `linear-gradient(44deg, ${preparedColors[0]} -6.77%, ${preparedColors[1]} 85.41%)`
    }
    if (preparedColors.length === 3) {
      return `linear-gradient(44deg, ${preparedColors[0]} 15.26%, ${preparedColors[1]} 49.98%, ${preparedColors[2]} 84.7%)`
    }
    if (preparedColors.length === 5) {
      return `linear-gradient(44deg, ${preparedColors[0]} 25.68%, ${preparedColors[1]} 36.63%, ${preparedColors[2]} 52.32%, ${preparedColors[3]} 64%, ${preparedColors[4]} 74.29%)`
    }

    const segments = preparedColors.flatMap((color, index) => {
      const percent = 100 / preparedColors.length
      return [`${color} ${percent * index}%`, `${color} ${percent * (index + 1)}%`]
    })
    return `linear-gradient(90deg, ${segments.join(', ')})`
  }

  setPropertyValue(value: string): void {
    const isImage = this.isImage(value)
    const isUrl = this.isUrl(value)
    const isGradient = this.isGradient(value)
    const isValidColor = this.isColor(value)
    const backgroundColor = this.dataset.sectionBackground || ''
    const backgroundColorName = hexToColorName(backgroundColor)

    if (isUrl) {
      this.element.removeAttribute('not-valid')
      this.element.style.setProperty('--gsc-color-swatch-value', `url(${value})`)
      this.setSimilarlyBgColorStatus(false)
      return
    }

    if (isImage) {
      this.setSimilarlyBgColorStatus(false)
      return
    }

    if (isGradient) {
      const gradientValue = this.getGradientValue(value.split('/'))
      const isSimilarlyBgColor =
        value.includes(backgroundColor) || value.includes(backgroundColorName)

      if (gradientValue) {
        this.element.removeAttribute('not-valid')
        this.element.style.setProperty('--gsc-color-swatch-value', gradientValue)
      }

      this.setSimilarlyBgColorStatus(isSimilarlyBgColor)
      return
    }

    const isSimilarlyBgColor =
      value === backgroundColor || value === backgroundColorName

    if (isValidColor) this.element.removeAttribute('not-valid')

    this.setSimilarlyBgColorStatus(isSimilarlyBgColor)
    this.element.style.setProperty('--gsc-color-swatch-value', value)
  }

  setSimilarlyBgColorStatus(isSimilarlyBgColor: boolean): void {
    this.element.toggleAttribute(
      'section-background-color-equally',
      isSimilarlyBgColor,
    )
  }

  isColor(color: string): boolean {
    const style = new Option().style
    style.color = color
    return style.color !== ''
  }

  isImage(value: string): boolean {
    return value.includes('.png') || value.includes('.jp')
  }

  isUrl(value: string): boolean {
    try {
      new URL(value)
      return true
    } catch {
      return false
    }
  }

  isGradient(value: string): boolean {
    return value.includes('/')
  }
}
